<script lang="ts">
import { computed, defineComponent, h, mergeProps, nextTick, ref, unref, watch } from 'vue';

import { CdsTransitionModal } from '@components/transitions';
import { classNames, focusableChildren, IN_BROWSER, omit } from '@components/utils';

import {
  makeDelayProps,
  makeDimensionProps,
  makeLazyProps,
  makeLoadingProps,
  makeOverlayProps,
  makeSizeProps,
  makeThemeProps,
  makeTransitionProps,
  provideTheme,
  useProxyModel,
  useRender,
  useScopeId,
  useSize
} from '../../composable';
import { CdsButton, CdsFunctionalButton } from '../CdsButton';
import { CdsContainer, CdsContainerItem } from '../CdsContainer';
import { CdsLoader } from '../CdsLoader';
import { CdsOverlay } from '../CdsOverlay';
import { makeActivatorProps, makeLocationStrategyProps, makeScrollStrategyProps } from '../CdsOverlay/composable';

import type { PropType, Component } from 'vue';
import type { ComponentProps } from '@components/utils';

export const componentName = 'CdsModal';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Модальное окно на весь экран
     */
    fullscreen: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Модальное окно с прокруткой
     */
    scrollable: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Сохранять фокус внутри модального окна, по-умолчанию true.
     * Его можно отключить, если необходимо использовать собственный элемент для фокусировки
     */
    retainFocus: {
      type: Boolean as PropType<boolean>,
      default: true
    },
    /**
     * !DEPRECATED! Показывать кнопку закрытия модального окна (или слот dismissBtn)
     */
    dismissBtn: {
      type: Boolean as PropType<boolean>,
      default: false
    },

    ...makeActivatorProps(),
    ...makeDimensionProps(),
    ...makeLazyProps(),
    ...makeDelayProps(),
    ...makeLoadingProps(),
    ...makeSizeProps(),
    ...makeThemeProps(),
    ...makeOverlayProps({
      zIndex: 2500
    }),
    ...makeLocationStrategyProps({
      origin: 'center center' as const
    }),
    ...makeScrollStrategyProps({
      scrollStrategy: 'block' as const
    }),
    ...makeTransitionProps({
      transition: { component: CdsTransitionModal as Component }
    })
  },
  emits: {
    /**
     * Событие изменения состояния модального окна
     * @param value
     */
    'update:modelValue': (value: boolean) => true
  },
  setup(props, { slots, emit, expose }) {
    const isActive = useProxyModel(props, 'modelValue');
    const { themeClasses } = provideTheme(props);
    const { sizeClasses } = useSize(props, componentName);
    const { scopeId } = useScopeId();

    const overlay = ref<typeof CdsOverlay>();

    function onFocusin(event: FocusEvent) {
      const before = event.relatedTarget as HTMLElement | null;
      const after = event.target as HTMLElement | null;

      if (
        before !== after &&
        unref(overlay)?.contentEl &&
        // We're the topmost modal
        unref(overlay)?.globalTop &&
        // It isn't the document or the modal body
        ![document, unref(overlay)!.contentEl].includes(after!) &&
        // It isn't inside the modal body
        !unref(overlay)!.contentEl.contains(after)
      ) {
        const focusable = focusableChildren(unref(overlay)!.contentEl);

        if (!focusable.length) return;

        const firstElement = focusable[0];
        const lastElement = focusable[focusable.length - 1];

        if (before === firstElement) {
          lastElement.focus();
        } else {
          firstElement.focus();
        }
      }
    }

    if (IN_BROWSER) {
      watch(
        () => unref(isActive) && props.retainFocus,
        (value) => {
          value ? document.addEventListener('focusin', onFocusin) : document.removeEventListener('focusin', onFocusin);
        },
        { immediate: true }
      );
    }

    watch(isActive, async (value) => {
      await nextTick();

      if (value) {
        unref(overlay)!.contentEl?.focus({ preventScroll: true });
      } else {
        unref(overlay)!.activatorEl?.focus({ preventScroll: true });
      }
    });

    const activatorProps = computed(() =>
      mergeProps(
        {
          'aria-haspopup': 'dialog',
          'aria-expanded': String(unref(isActive))
        },
        props.activatorProps
      )
    );

    const dismissBtnProps = computed<ComponentProps<typeof CdsButton>>(() => ({
      icon: 'remove',
      class: 'cds-modal__dismiss-btn',
      onClick: () => (isActive.value = false)
    }));

    useRender(() => {
      const overlayProps = omit(props, ['fullscreen', 'retainFocus', 'scrollable', 'dismissBtn']);

      return h(
        CdsOverlay,
        {
          ref: overlay,
          class: classNames('cds-modal', unref(sizeClasses), unref(themeClasses), {
            'cds-modal--fullscreen': props.fullscreen,
            'cds-modal--scrollable': props.scrollable,
            'cds-modal--loading': props.loading,
            'cds-modal--has-dismiss-btn': props.dismissBtn
          }),
          ...overlayProps,
          activatorProps: unref(activatorProps),
          modalValue: unref(isActive),
          'onUpdate:modelValue': (value: boolean) => {
            isActive.value = value;
            emit('update:modelValue', value);
          },
          'aria-modal': 'true',
          role: 'dialog',
          ...scopeId
        },
        {
          activator: slots.activator,
          default: (...args: any) =>
            h(
              CdsContainer,
              { class: 'cds-modal__container' },
              {
                default: () =>
                  h(
                    CdsContainerItem,
                    {},
                    {
                      default: () => [
                        props.loading &&
                          h(CdsLoader, {
                            fixed: true,
                            overlay: true
                          }),
                        props.dismissBtn
                          ? h(CdsFunctionalButton, unref(dismissBtnProps))
                          : slots.dismissBtn?.({ props: unref(dismissBtnProps) }),
                        slots.default?.(...args)
                      ]
                    }
                  )
              }
            )
        }
      );
    });

    expose({
      overlay
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsModal';
</style>
